// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Daily Catch  (status strip at the top of pages/DailyCatch.tsx)
// The drop's label ("Batch #04"), the staff note, a live dispatch countdown (only when staff set a dispatch time), the
// share of released packs already claimed (computed by the backend from the real stock, never typed in) and the
// connection state ("Live · updated 4 s ago" / "Reconnecting…"). The countdown runs off the SERVER clock (useDrop.now).
// Deliberately NOT here: cold-chain / formalin-tested claims, "iced under 60 mins".
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useState } from "react";
import { Clock, Radio, WifiOff } from "lucide-react";
import { formatCountdown, type DropView } from "../../utils/drop";

interface DropStatusProps {
  data: DropView;
  now: () => number;
  updatedAt: number | null;
  stale: boolean;
}

export default function DropStatus({ data, now, updatedAt, stale }: DropStatusProps) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const timer = window.setInterval(() => setTick((n) => n + 1), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const drop = data.drop;
  if (!drop) return null;
  const remaining = drop.dispatchAt ? new Date(drop.dispatchAt).getTime() - now() : null;
  const countdown = remaining === null ? null : formatCountdown(remaining);
  const ago = updatedAt === null ? null : Math.max(0, Math.round((Date.now() - updatedAt) / 1000));

  return (
    <section aria-label="Live drop status" className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-teal-700 dark:text-teal-300">
          <span aria-hidden="true" className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-teal-500 opacity-60 motion-reduce:animate-none" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-teal-600" />
          </span>
          Live harbour landings
        </p>
        <span className="rounded-full bg-ocean-800 px-3 py-1 text-xs font-bold text-white">{drop.label}</span>
      </div>

      {drop.note && <p className="mt-2 text-sm text-ocean-800/90 dark:text-foam-100/85">{drop.note}</p>}

      <div className="mt-3 flex items-center justify-between gap-3 text-sm font-semibold text-ocean-950 dark:text-foam-50">
        <span>Claimed so far</span>
        <span className="tabular-nums">{data.claimedPercent}%</span>
      </div>
      <div
        role="progressbar"
        aria-label="Packs claimed"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={data.claimedPercent}
        className="mt-1 h-2.5 overflow-hidden rounded-full bg-ocean-700/10 dark:bg-white/10"
      >
        <div className="h-full rounded-full bg-teal-600 transition-[width] duration-500 motion-reduce:transition-none" style={{ width: `${data.claimedPercent}%` }} />
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-ocean-800/80 dark:text-foam-100/75">
        {remaining !== null ? (
          <span className="inline-flex items-center gap-1.5 font-semibold text-coral-500 dark:text-coral-300">
            <Clock className="h-3.5 w-3.5" aria-hidden="true" />
            {countdown ? <>Dispatch in <span className="tabular-nums">{countdown}</span></> : "Dispatch window closed"}
          </span>
        ) : (
          <span />
        )}
        {stale ? (
          <span role="status" className="inline-flex items-center gap-1.5 font-semibold">
            <WifiOff className="h-3.5 w-3.5" aria-hidden="true" />
            Reconnecting… showing the last update
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5">
            <Radio className="h-3.5 w-3.5" aria-hidden="true" />
            Live{ago !== null && <> · updated <span className="tabular-nums">{ago}</span> s ago</>}
          </span>
        )}
      </div>
    </section>
  );
}
